import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateSmartDevice } from "@/hooks/useSmartDevices";

interface AddSmartDeviceDialogProps {
  providerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeviceAdded: () => void;
}

const AddSmartDeviceDialog = ({
  providerId,
  open,
  onOpenChange,
  onDeviceAdded,
}: AddSmartDeviceDialogProps) => {
  const [deviceName, setDeviceName] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [preheatMinutes, setPreheatMinutes] = useState(30);
  const createDevice = useCreateSmartDevice();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!deviceName.trim() || !deviceId.trim()) {
      return;
    }

    try {
      await createDevice.mutateAsync({
        provider_id: providerId,
        device_name: deviceName.trim(),
        device_id: deviceId.trim(),
        is_enabled: true,
        preheat_minutes: preheatMinutes,
      });

      setDeviceName("");
      setDeviceId("");
      setPreheatMinutes(30);
      onOpenChange(false);
      onDeviceAdded();
    } catch (error) {
      console.error('Error creating device:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Sauna Control Device</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="device-name">Device Name</Label>
            <Input
              id="device-name"
              placeholder="e.g., Main Sauna Control"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="device-id">Device ID</Label>
            <Input
              id="device-id"
              placeholder="e.g., abc123"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              required
            />
            <p className="text-sm text-muted-foreground">
              This will be used to connect to https://api-{deviceId || "IDHERE"}.wellsuites.co/
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="preheat-minutes">Preheat Time (minutes)</Label>
            <Input
              id="preheat-minutes"
              type="number"
              min={5}
              max={120}
              value={preheatMinutes}
              onChange={(e) => setPreheatMinutes(parseInt(e.target.value) || 30)}
            />
            <p className="text-sm text-muted-foreground">
              How many minutes before a booking to start preheating the sauna.
            </p>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createDevice.isPending}>
              {createDevice.isPending ? "Adding..." : "Add Device"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSmartDeviceDialog;
